export const formatCurrency = (value: any) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);

/**
 * Parsing decimale che accetta sia la virgola sia il punto (tastiere italiane, copia/incolla).
 * Da usare al posto di parseFloat sui valori inseriti dall'utente.
 * Input vuoto o non numerico → 0, così nei totali non entrano NaN; i negativi diventano 0.
 */
export const parseDecimal = (value: string | number | null | undefined): number => {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return 0;
        return value < 0 ? 0 : value;
    }
    const normalized = String(value).trim().replace(',', '.');
    const n = parseFloat(normalized);
    if (!Number.isFinite(n)) return 0;
    return n < 0 ? 0 : n;
};

/**
 * Formato italiano (virgola decimale) per numeri in sola lettura, non per gli input.
 */
export const formatDecimal = (value: number | null | undefined, decimals = 2): string => {
    if (value == null || !Number.isFinite(value)) return '';
    return value.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

/**
 * Arrotonda ore decimali a passi di 10 minuti: sotto i 5 minuti per difetto, da 5 in su per eccesso
 * (62 min → 60, 65 min → 70). Va usato solo sulle somme aggregate, i singoli record restano precisi.
 * Stessa logica di EconomicUtils.roundHoursTo10Min lato server.
 */
export const roundHoursTo10Min = (hours: number | null | undefined): number => {
    if (hours == null || !Number.isFinite(hours) || hours <= 0) return 0;
    const totalMinutes = Math.round(hours * 60);
    const blocks = Math.floor((totalMinutes + 5) / 10);
    return (blocks * 10) / 60;
};

/**
 * Data fine per un preset di durata, a partire dal giorno di dataInizio, in formato datetime-local.
 * daysOffset parte da 0 (0 = stesso giorno, 6 = sette giorni); l'ora è sempre 23:59
 * e il backend divide gli incarichi intermedi in giornate 00:00-23:59.
 */
export const computeDataFinePreset = (dataInizio: string, daysOffset: number): string => {
    if (!dataInizio) return '';
    const [datePart] = dataInizio.split('T');
    if (!datePart) return '';
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return '';
    const target = new Date(y, m - 1, d);
    target.setDate(target.getDate() + daysOffset);
    const yy = target.getFullYear();
    const mm = String(target.getMonth() + 1).padStart(2, '0');
    const dd = String(target.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}T23:59`;
};

export const DATA_FINE_PRESETS: ReadonlyArray<{ label: string; daysOffset: number }> = [
    { label: 'Oggi',      daysOffset: 0 },
    { label: 'Domani',    daysOffset: 1 },
    { label: '3 giorni',  daysOffset: 2 },
    { label: '7 giorni',  daysOffset: 6 },
    { label: '15 giorni', daysOffset: 14 },
    { label: '30 giorni', daysOffset: 29 },
];

/**
 * True se dataFine cade il giorno dopo dataInizio: solo in quel caso si può
 * scegliere "Continuativo" (un unico incarico a cavallo della mezzanotte).
 */
export const isGiornoSuccessivo = (dataInizio?: string, dataFine?: string): boolean => {
    if (!dataInizio || !dataFine) return false;
    const [y, m, d] = dataInizio.slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return false;
    const next = new Date(y, m - 1, d + 1);
    const nextStr = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
    return dataFine.slice(0, 10) === nextStr;
};

/**
 * "YYYY-MM-DD" di una data nel fuso del browser. toISOString() darebbe il giorno
 * UTC, che tra mezzanotte e le 2 (ora italiana) è ancora quello prima.
 */
export const localIsoDate = (d: Date = new Date()): string => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

// Istante corrente in formato datetime-local, usato dai preset quando dataInizio è vuota.
export const nowDatetimeLocal = (): string => toDatetimeLocal(new Date());

// Data in formato datetime-local ("YYYY-MM-DDTHH:mm") nel fuso del browser.
export const toDatetimeLocal = (d: Date): string => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

/**
 * Converte una data ISO (YYYY-MM-DD, con o senza orario) in DD/MM/YYYY per i PDF.
 * Se è già in formato italiano o non è riconoscibile la restituisce com'è; vuoto → ''.
 */
export const formatIsoDateToItalian = (val: any): string => {
    if (val == null) return '';
    const s = String(val).trim();
    if (!s) return '';
    if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) return s;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return s;
};

/**
 * Formatta ore decimali come "Xh Ym" (es. 1.5 → "1h 30m", 0 o null → "0m").
 */
export const formatOreInHM = (h: number | null | undefined, roundTo10Min = false): string => {
    // Di default mostra i minuti reali: il backend salva a passi di 0,1h e un ulteriore
    // arrotondamento farebbe divergere KPI e dettaglio. Con roundTo10Min=true arrotonda
    // a 10 minuti, per i report mensili dell'amministrazione.
    const exactMin = (Number(h) || 0) * 60;
    // Stesso algoritmo di roundHoursTo10Min: minuti interi, poi blocchi da 10 con arrotondamento half-up.
    const roundedMin = Math.round(exactMin);
    const totalMin = roundTo10Min
        ? Math.floor((roundedMin + 5) / 10) * 10
        : roundedMin;
    if (totalMin <= 0) return '0m';
    const hh = Math.floor(totalMin / 60);
    const mm = totalMin % 60;
    if (hh === 0) return `${mm}m`;
    if (mm === 0) return `${hh}h`;
    return `${hh}h ${mm}m`;
};

/**
 * Parte variabile del nome dei PDF del mandato (Mandato_<x>.pdf, DatiSoggetto_<x>.pdf): Nr. Mandato
 * inserito in creazione, ripulito per i nomi di file; se manca o è automatico ("temp<n>") l'id,
 * altrimenti "bozza". Mai il nome del soggetto o del cliente. Stessa regola del BE (NomeFileMandato).
 */
export const nomeBaseFileMandato = (nrMandato: any, id?: number | null): string => {
    const nr = nrMandato == null ? '' : String(nrMandato).trim();
    const pulito = nr.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/-{2,}/g, '-').replace(/^[-._]+|[-._]+$/g, '').slice(0, 60);
    if (!pulito || /^(temp\d*|na|n\/a)$/i.test(nr)) return id != null ? String(id) : 'bozza';
    return pulito;
};

/**
 * Estrae solo il numero del mandato/pratica dalle varie forme che arrivano dal BE
 * (es. "Mandato 5/2026 - Mario Rossi" → "5/2026", "Pratica #42" → "42", vuoto → "-").
 */
export const estraiNumeroMandato = (val: any): string => {
    if (val == null || val === '') return '-';
    const s = String(val).trim();
    if (!s) return '-';
    const left = s.split(' - ')[0];
    const cleaned = left.replace(/^(Mandato|Pratica)[\s#]*/i, '').trim();
    return cleaned || '-';
};

/**
 * Sceglie il PDF da mandare a firma OTP: il più recente non firmato che contiene `keyword`
 * (in minuscolo, es. 'mandato', 'soggetto'). Esclude i file "firmato" e quelli non PDF
 * (XML di audit, foto documento). Restituisce undefined se non ce n'è nessuno.
 */
export const selectPdfDaFirmare = <T extends { name?: string; createdAt?: string }>(
    files: T[] | null | undefined,
    keyword: string,
): T | undefined => {
    if (!files || files.length === 0) return undefined;
    const k = keyword.toLowerCase();
    const candidati = files.filter(f => {
        const n = (f.name || '').toLowerCase();
        return n.includes(k) && n.endsWith('.pdf') && !n.includes('firmato');
    });
    if (candidati.length === 0) return undefined;
    // Più recente per createdAt; i file senza data vanno in fondo.
    return candidati.slice().sort((a, b) =>
        (b.createdAt || '').localeCompare(a.createdAt || ''),
    )[0];
};

/** PDF mandato più recente non firmato. */
export const selectMandatoPdf = <T extends { name?: string; createdAt?: string }>(
    files: T[] | null | undefined,
): T | undefined => selectPdfDaFirmare(files, 'mandato');

/** PDF dati soggetto più recente non firmato. */
export const selectSoggettoPdf = <T extends { name?: string; createdAt?: string }>(
    files: T[] | null | undefined,
): T | undefined => selectPdfDaFirmare(files, 'soggetto');

// Etichette dei ruoli per la UI: l'enum resta ADMIN/RESPONSABILE/COLLABORATORE,
// ma ADMIN si mostra come "Titolare". Il valore raw va solo nei `value` dei form.
export const formatRole = (role: string | null | undefined): string => {
    if (!role) return '';
    switch (role.toUpperCase()) {
        case 'ADMIN':         return 'Titolare';
        case 'RESPONSABILE':  return 'Responsabile';
        case 'COLLABORATORE': return 'Collaboratore';
        default:              return role;
    }
};

/**
 * Prova a ricavare la data di scatto di un'immagine o di un video.
 * Restituisce una stringa per datetime-local o null se non affidabile.
 */
export const checkExifDate = async (file: File): Promise<string | null> => {
    // Immagini: si usa lastModified (per i dati EXIF servirebbe una libreria apposita).
    if (file.type.startsWith('image/')) {
        // Da mobile lastModified di solito è corretto, salvo file passati da WhatsApp Web.
        const date = new Date(file.lastModified);
        const now = new Date();

        // Data troppo recente (es. file appena scaricato) o assente: la consideriamo mancante.
        if (Math.abs(now.getTime() - date.getTime()) < 60000) {
            return null;
        }
        return toDatetimeLocal(date);
    }

    // Video
    if (file.type.startsWith('video/')) {
        // Il browser non espone i metadati video senza librerie: si usa la data del file se plausibile.
        const date = new Date(file.lastModified);
        if (date > new Date(2000, 0, 1)) return toDatetimeLocal(date);
    }

    return null;
};